import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  ShieldCheck, Award, Globe, MapPin, ArrowRight, CheckCircle2, Star,
} from "lucide-react";
import PartnerReviews from "./PartnerReviews";

export default function PartnerStorefront() {
  const { slug } = useParams();
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);

  useEffect(() => {
    api.get(`/api/partners/public/${slug}`)
      .then(({ data }) => setData(data))
      .catch(() => setErr("not_found"));
  }, [slug]);

  if (err === "not_found") {
    return (
      <div className="min-h-screen bg-[#F7F5F0] grid place-items-center p-6">
        <Card className="max-w-md" data-testid="storefront-not-found">
          <CardContent className="p-8 text-center">
            <div className="text-xl font-bold mb-2">Partner not found</div>
            <div className="text-sm text-gray-500 mb-4">
              This partner isn't approved yet or the link is incorrect.
            </div>
            <Link to="/partners" className="text-sm text-blue-600 hover:underline">
              ← Browse all partners
            </Link>
          </CardContent>
        </Card>
      </div>
    );
  }
  if (!data) return <div className="p-12 text-center">Loading…</div>;

  const { partner, courses } = data;

  return (
    <div className="min-h-screen bg-[#F7F5F0]" data-testid="storefront-root">
      {/* ---------- Hero ---------- */}
      <section className="bg-gradient-to-br from-[#064E3B] to-[#065F46] text-white pt-16 pb-24 px-6 relative overflow-hidden">
        <div
          aria-hidden
          className="absolute inset-0 opacity-[0.08] pointer-events-none"
          style={{ backgroundImage:
            "radial-gradient(circle at 20% 40%, #F59E0B 0, transparent 40%), radial-gradient(circle at 80% 60%, #DC2626 0, transparent 40%)" }}
        />
        <div className="max-w-6xl mx-auto relative">
          <Link to="/partners" className="text-xs text-white/70 hover:text-white inline-flex items-center gap-1 mb-6"
            data-testid="storefront-breadcrumb-partners">
            ← All training partners
          </Link>

          <div className="flex flex-col md:flex-row md:items-start gap-8">
            <div className="flex-1">
              <div className="inline-flex items-center gap-2 bg-white/10 backdrop-blur border border-white/20 px-3 py-1 rounded-full text-xs font-semibold mb-4"
                data-testid="storefront-dual-brand-pill">
                <ShieldCheck className="w-3.5 h-3.5 text-[#F59E0B]" />
                {partner.organization_name} × Teacher4Me
              </div>

              <h1 className="font-display font-black text-4xl sm:text-5xl lg:text-6xl leading-tight tracking-tight"
                data-testid="storefront-org-name">
                {partner.organization_name}
              </h1>
              {partner.tagline && (
                <p className="text-lg sm:text-xl text-white/85 mt-4 max-w-2xl leading-relaxed"
                  data-testid="storefront-tagline">
                  {partner.tagline}
                </p>
              )}

              <div className="flex flex-wrap gap-2 mt-5">
                {partner.review_count > 0 && (
                  <a href="#reviews">
                    <Badge className="bg-white/10 text-white border-white/20 gap-1 text-xs hover:bg-white/20 transition-colors cursor-pointer"
                      data-testid="storefront-hero-rating">
                      <Star className="w-3 h-3 fill-[#F59E0B] text-[#F59E0B]" />
                      {partner.rating_avg?.toFixed(1)} ({partner.review_count} review{partner.review_count === 1 ? "" : "s"})
                    </Badge>
                  </a>
                )}
                {partner.country && (
                  <Badge className="bg-white/10 text-white border-white/20 gap-1 text-xs">
                    <Globe className="w-3 h-3" />
                    {partner.country}{partner.province ? ` · ${partner.province}` : ""}
                  </Badge>
                )}
                {(partner.accreditations || []).slice(0, 3).map((a) => (
                  <Badge key={a} className="bg-white/10 text-white border-white/20 gap-1 text-xs">
                    <Award className="w-3 h-3" /> {a}
                  </Badge>
                ))}
              </div>

              <div className="flex flex-wrap gap-3 mt-8">
                <a href="#catalog">
                  <Button className="bg-[#F59E0B] hover:bg-[#D97706] text-black font-semibold rounded-full px-8 py-6"
                    data-testid="storefront-cta-catalog-btn">
                    Browse {courses.length} course{courses.length === 1 ? "" : "s"}
                  </Button>
                </a>
                {partner.website && (
                  <a href={partner.website} target="_blank" rel="noreferrer">
                    <Button variant="outline"
                      className="bg-transparent border-white/40 text-white hover:bg-white/10 rounded-full px-8 py-6"
                      data-testid="storefront-cta-website-btn">
                      Visit {new URL(partner.website).hostname}
                    </Button>
                  </a>
                )}
              </div>
            </div>

            {partner.logo_url && (
              <div className="w-32 md:w-40 shrink-0 bg-white rounded-2xl p-4 shadow-2xl">
                <img src={partner.logo_url} alt={partner.organization_name}
                  className="w-full h-auto object-contain"
                  data-testid="storefront-logo" />
              </div>
            )}
          </div>
        </div>
      </section>

      {/* ---------- Dual-brand promise ---------- */}
      <section className="max-w-5xl mx-auto -mt-14 px-6" data-testid="storefront-promise">
        <Card className="border-none shadow-xl">
          <CardContent className="p-6 md:p-8 grid md:grid-cols-3 gap-6 items-center">
            <div>
              <div className="text-xs uppercase text-gray-500 tracking-wider mb-1">Issued by</div>
              <div className="font-bold text-lg text-[#064E3B]">{partner.organization_name}</div>
            </div>
            <div className="text-center text-3xl text-gray-300 hidden md:block">+</div>
            <div className="text-right md:text-left">
              <div className="text-xs uppercase text-gray-500 tracking-wider mb-1">In partnership with</div>
              <div className="font-bold text-lg text-[#D92D20]">Teacher4Me.ca</div>
            </div>
          </CardContent>
          <div className="border-t px-6 md:px-8 py-4 bg-[#F0FDF4] rounded-b-lg flex items-start gap-2">
            <ShieldCheck className="w-4 h-4 text-[#065F46] mt-0.5 shrink-0" />
            <p className="text-sm text-[#065F46]">
              Every completion earns a <b>dual-brand certificate</b> with QR verification — proof
              that you finished a real course, backed by a real training organization.
            </p>
          </div>
        </Card>
      </section>

      {/* ---------- About ---------- */}
      {partner.about && (
        <section className="max-w-4xl mx-auto py-16 px-6" data-testid="storefront-about">
          <h2 className="font-display font-bold text-2xl mb-4">About {partner.organization_name}</h2>
          <p className="text-[#4B5563] leading-relaxed whitespace-pre-line">{partner.about}</p>
        </section>
      )}

      {/* ---------- Accreditations ---------- */}
      {(partner.accreditations || []).length > 0 && (
        <section className="max-w-4xl mx-auto pb-8 px-6" data-testid="storefront-accreditations">
          <h3 className="font-display font-bold text-lg mb-3">Accreditations & memberships</h3>
          <div className="grid sm:grid-cols-2 gap-2">
            {partner.accreditations.map((a) => (
              <div key={a} className="flex items-start gap-2 text-sm">
                <CheckCircle2 className="w-4 h-4 text-[#064E3B] mt-0.5 shrink-0" />
                <span>{a}</span>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* ---------- Catalog ---------- */}
      <section id="catalog" className="max-w-6xl mx-auto py-12 px-6" data-testid="storefront-catalog">
        <div className="flex items-baseline justify-between mb-6">
          <h2 className="font-display font-bold text-3xl">Course catalog</h2>
          <div className="text-sm text-gray-500">
            {courses.length} course{courses.length === 1 ? "" : "s"} available
          </div>
        </div>

        {courses.length === 0 ? (
          <Card><CardContent className="p-10 text-center text-gray-500"
            data-testid="storefront-catalog-empty">
            {partner.organization_name} hasn't published any courses yet — check back soon.
          </CardContent></Card>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
            {courses.map((c) => (
              <Link key={c.id} to={`/courses/${c.slug}`}
                data-testid="storefront-course-card"
                className="group">
                <Card className="overflow-hidden hover:shadow-lg transition-shadow h-full flex flex-col">
                  {c.thumbnail_url && (
                    <div className="aspect-video overflow-hidden bg-gray-100">
                      <img src={c.thumbnail_url} alt={c.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform" />
                    </div>
                  )}
                  <CardContent className="p-5 flex-1 flex flex-col">
                    <div className="flex items-center gap-2 text-xs text-gray-500 mb-2">
                      <Badge variant="secondary" className="text-[10px]">{c.level}</Badge>
                      <span>·</span>
                      <span>{c.duration_hours}h</span>
                    </div>
                    <h3 className="font-bold text-base leading-snug mb-1">{c.title}</h3>
                    <p className="text-sm text-gray-600 line-clamp-2 flex-1">{c.short_description}</p>
                    <div className="flex items-center justify-between mt-4 pt-4 border-t">
                      <div className="text-lg font-bold text-[#DC2626]">
                        ${c.price_usd}
                      </div>
                      <span className="text-xs font-semibold text-[#064E3B] inline-flex items-center gap-1 group-hover:gap-2 transition-all">
                        View details <ArrowRight className="w-3 h-3" />
                      </span>
                    </div>
                  </CardContent>
                </Card>
              </Link>
            ))}
          </div>
        )}
      </section>

      {/* ---------- Reviews ---------- */}
      <div id="reviews">
        <PartnerReviews partnerId={partner.id} partnerName={partner.organization_name} />
      </div>

      {/* ---------- Trust footer ---------- */}
      <section className="bg-white border-t border-gray-200 py-10 px-6 mt-8">
        <div className="max-w-4xl mx-auto grid md:grid-cols-3 gap-6 text-center">
          <div>
            <Star className="w-6 h-6 mx-auto text-[#F59E0B] mb-2" />
            <div className="font-semibold text-sm">Selectively curated</div>
            <div className="text-xs text-gray-500 mt-1">Every partner is manually vetted before publishing.</div>
          </div>
          <div>
            <ShieldCheck className="w-6 h-6 mx-auto text-[#064E3B] mb-2" />
            <div className="font-semibold text-sm">Dual-certified</div>
            <div className="text-xs text-gray-500 mt-1">Two-name certificate + QR verify page for every completion.</div>
          </div>
          <div>
            <MapPin className="w-6 h-6 mx-auto text-[#DC2626] mb-2" />
            <div className="font-semibold text-sm">Made in Canada</div>
            <div className="text-xs text-gray-500 mt-1">Hosted by Teacher4Me.ca — Alberta-based, PIPEDA-compliant.</div>
          </div>
        </div>
      </section>
    </div>
  );
}
